import type {
  EditDlnItemLinksMutationOptions,
  EditDlnItemLinksVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysInvoice } from '../queryKeys';

import { DLN_ITEM_LINK_MUTATION_KEY } from './constants';
import { editDlnItemLinks } from './editDlnItemLinks';
import { writeInvoiceDetail } from './writeInvoiceDetail';

/**
 * Adds and removes manual delivery note links of an invoice in one request.
 * The invoice in the response replaces the cached detail, and every
 * linkable-line query is refetched.
 *
 * @param options - Optional mutation configuration; callbacks run after the hook's own.
 * @returns The mutation; `isPending` lasts until the caches are settled.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/edit_dln_item_links_invoices__invoice_id__dln_item_links_patch
 */
export function useMutationEditDlnItemLinks(
  options?: EditDlnItemLinksMutationOptions,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (variables: EditDlnItemLinksVariables) =>
      editDlnItemLinks(variables),
    mutationKey: DLN_ITEM_LINK_MUTATION_KEY,
    onSettled(data, error, variables, onMutateResult, context) {
      return Promise.all([
        writeInvoiceDetail(queryClient, variables.invoiceId, data?.invoice),
        queryClient.invalidateQueries({
          queryKey: queryKeysInvoice.getLinkableDlnItemsAll(),
        }),
        options?.onSettled?.(data, error, variables, onMutateResult, context),
      ]);
    },
  });
}
